import { Link, useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { OFFERS_DISCLOSURE, type PropertyOffersResponse, type PropertyPublic } from "@townsy/contracts";
import { usePropertyQuery } from "../../api/queries";
import { usePropertyOffersQuery } from "../../api/rewardsQueries";
import { isRewardsUnavailable } from "../../api/rewards";
import { describeRewardError } from "../../rewards/errors";
import { dailyProgress, earnRows } from "../../rewards/earnList";
import { useTownStore } from "../../state/store";
import { OfferListView, type OfferCardActions } from "./OfferList";
import { StoreOffersPanelSection } from "./offers/OfferSetupEntry";
import { useRewardNav } from "./useRewardNav";

/**
 * A store's sponsored tasks (ADR-0011 #7): in the property panel ("Sponsored tasks" section) and inside
 * the store (HUD "Tasks" button -> side panel / bottom sheet). The sponsorship disclosure is always
 * visible. A store without tasks says so (docs/01 "Interior": no fake rewards); a Townsy building only
 * shows the section when it runs a house task.
 */

export const NO_TASKS_TEXT = "This store has no sponsored tasks right now.";

/** The list body for one property (presentational wrapper around the server's response). */
export function PropertyTasksView({
  data,
  actions,
  state,
  error,
  onRetry,
}: {
  data: PropertyOffersResponse | null;
  actions: OfferCardActions;
  state: "loading" | "error" | "unavailable" | "ready";
  error?: string | null;
  onRetry?: () => void;
}) {
  if (state === "loading") return <p className="muted small">Loading tasks…</p>;
  if (state === "unavailable") {
    return (
      <p className="muted small" style={{ margin: 0 }} data-testid="tasks-unavailable">
        Sponsored tasks are not available on this server yet.
      </p>
    );
  }
  if (state === "error" || !data) {
    return (
      <p className="small" style={{ margin: 0 }}>
        <span className="error-text">{error ?? "Couldn't load the tasks."}</span>{" "}
        {onRetry ? (
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </p>
    );
  }
  const rows = earnRows(data.items, { daily: data.daily });
  return (
    <OfferListView
      rows={rows}
      disclosure={data.disclosure || OFFERS_DISCLOSURE}
      actions={actions}
      progress={dailyProgress(data.daily)}
      emptyText={NO_TASKS_TEXT}
    />
  );
}

function usePropertyTasks(propertyId: string, enabled = true) {
  const q = usePropertyOffersQuery(propertyId, enabled);
  const nav = useRewardNav();
  const actions: OfferCardActions = {
    onPrimary: (row) => nav.start(row.offer),
    onShowActive: () => nav.openEarn(),
  };
  const state: "loading" | "error" | "unavailable" | "ready" = q.data ? "ready" : q.error ? (isRewardsUnavailable(q.error) ? "unavailable" : "error") : "loading";
  const error = q.error ? describeRewardError(q.error, { stage: "load" }).message : null;
  return { q, actions, state, error };
}

/** Property panel section (stores always; Townsy buildings only with a house task). */
export function PropertyTasksSection({ item }: { item: Pick<PropertyPublic, "id" | "type" | "permittedActions"> }) {
  const signedIn = useTownStore((s) => Boolean(s.bootstrap?.session.userId));
  const own = usePropertyQuery(signedIn && item.type === "COMMERCIAL" ? item.id : null);
  const { q, actions, state, error } = usePropertyTasks(item.id, item.type !== "RESIDENTIAL");
  if (item.type === "RESIDENTIAL") return null;
  if (item.type === "CIVIC" && !(q.data && q.data.items.length > 0)) return null;
  const isOwner = Boolean(own.data?.permittedActions.includes("edit_interior"));
  const count = q.data?.items.length ?? 0;
  return (
    <div className="section" data-testid="property-tasks">
      <div className="section-title">Sponsored tasks{count > 0 ? ` (${count})` : ""}</div>
      <PropertyTasksView data={q.data ?? null} actions={actions} state={state} error={error} onRetry={() => void q.refetch()} />
      {/* ADR-0027: the owner adds and edits offers on one screen, with one plain status. */}
      {item.type === "COMMERCIAL" ? <StoreOffersPanelSection propertyId={item.id} isOwner={isOwner} /> : null}
      {isOwner ? (
        <p className="small" style={{ margin: "8px 0 0" }}>
          <Link to="/advertise?tab=tasks" data-testid="manage-tasks">
            Advertiser tools
          </Link>{" "}
          <span className="muted">(partner tasks, reports and partner keys)</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Inside a store: its tasks in a side panel (a bottom sheet on phones), opened from the HUD "Tasks"
 * button. Hidden while the property panel is open on a phone (one sheet at a time).
 */
export function StoreTasksPanel() {
  const { propertyId, open, selected, chatOpen } = useTownStore(
    useShallow((s) => {
      const inside = s.room.kind === "interior" ? s.room.propertyId : null;
      return {
        propertyId: inside,
        open: Boolean(inside && s.ui.storeTasksFor === inside),
        selected: s.ui.selectedPropertyId,
        chatOpen: s.ui.chatOpen,
      };
    }),
  );
  const name = useTownStore((s) => {
    const p = propertyId ? s.properties[propertyId] : null;
    return p?.placement?.businessName ?? p?.civic?.name ?? p?.address ?? "This place";
  });
  const setUi = useTownStore((s) => s.setUi);
  const tasks = usePropertyTasks(propertyId ?? "none", open && Boolean(propertyId));
  // The property panel uses the same side (and the one bottom sheet on phones): it wins while open.
  if (!open || !propertyId || selected) return null;
  return (
    <section className={`panel panel-right store-tasks-panel${chatOpen ? " compact" : ""}`} role="region" aria-labelledby="store-tasks-title" data-testid="store-tasks-panel">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="store-tasks-title">Tasks</h2>
          <div className="small muted">{name}</div>
        </div>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close tasks" onClick={() => setUi({ storeTasksFor: null })}>
          ×
        </button>
      </div>
      <div className="panel-body">
        <PropertyTasksView
          data={tasks.q.data ?? null}
          actions={tasks.actions}
          state={tasks.state}
          error={tasks.error}
          onRetry={() => void tasks.q.refetch()}
        />
      </div>
    </section>
  );
}

/** HUD button inside a store: "Tasks" with the number of live tasks (the list explains each one). */
export function StoreTasksButton() {
  const { propertyId, open } = useTownStore(
    useShallow((s) => {
      const inside = s.room.kind === "interior" ? s.room.propertyId : null;
      return { propertyId: inside, open: Boolean(inside && s.ui.storeTasksFor === inside) };
    }),
  );
  const type = useTownStore((s) => (propertyId ? (s.properties[propertyId]?.type ?? null) : null));
  const selected = useTownStore((s) => s.ui.selectedPropertyId);
  const setUi = useTownStore((s) => s.setUi);
  const navigate = useNavigate();
  const enabled = Boolean(propertyId) && (type === "COMMERCIAL" || type === "CIVIC");
  const q = usePropertyOffersQuery(enabled ? propertyId : null, enabled);
  const count = q.data?.items.length ?? 0;
  // Townsy buildings only get the button when they run a house task; stores always (and say when they have none).
  if (!enabled || (type === "CIVIC" && count === 0) || (q.error && isRewardsUnavailable(q.error))) return null;
  return (
    <button
      type="button"
      className="btn btn-sm"
      aria-pressed={open}
      onClick={() => {
        setUi({ storeTasksFor: open ? null : propertyId });
        // The property panel shares the side of the screen: close it so the tasks show.
        if (!open && selected) navigate("/");
      }}
      aria-label={count > 0 ? `Tasks, ${count} available` : "Tasks"}
      data-testid="hud-store-tasks"
    >
      Tasks{count > 0 ? <span className="badge badge-coin">{count}</span> : null}
    </button>
  );
}
